/**
 * useSoundEffects.js
 *
 * Hook de sons légers via Web Audio API (aucune dépendance, aucun asset).
 * Utilisé pour confirmer les bonnes/mauvaises réponses en mode présentation.
 *
 * - playSuccess() : double tone montant joyeux (C5 → E5)
 * - playError()   : tone descendant atténué (A4 → F4)
 * - playClick()   : tick court neutre
 * - mute()/unmute() : persistance localStorage
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'c2r_sound_muted';

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  return new Ctx();
}

export default function useSoundEffects() {
  const ctxRef = useRef(null);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, muted ? '1' : '0'); } catch { /* ignore */ }
  }, [muted]);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) ctxRef.current = getAudioContext();
    if (ctxRef.current && ctxRef.current.state === 'suspended') {
      ctxRef.current.resume().catch(() => {});
    }
    return ctxRef.current;
  }, []);

  const tone = useCallback((freq, duration = 0.15, type = 'sine', volume = 0.18, when = 0) => {
    if (muted) return;
    const ctx = ensureCtx();
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      gain.gain.value = 0;
      const t0 = ctx.currentTime + when;
      gain.gain.linearRampToValueAtTime(volume, t0 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + duration);
    } catch { /* ignore */ }
  }, [muted, ensureCtx]);

  const playSuccess = useCallback(() => {
    // Do5 (523) → Mi5 (659) montée joyeuse
    tone(523, 0.13, 'sine', 0.16, 0);
    tone(659, 0.18, 'sine', 0.18, 0.12);
    tone(784, 0.22, 'sine', 0.18, 0.24); // Sol5 final
  }, [tone]);

  const playError = useCallback(() => {
    // La4 → Fa4 descente
    tone(440, 0.15, 'triangle', 0.18, 0);
    tone(349, 0.22, 'triangle', 0.18, 0.13);
  }, [tone]);

  const playClick = useCallback(() => {
    tone(880, 0.04, 'square', 0.07, 0);
  }, [tone]);

  return {
    muted,
    toggleMute: () => setMuted((m) => !m),
    setMuted,
    playSuccess,
    playError,
    playClick,
  };
}
